import { Clock, Pencil, PlusIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { getWorstCriticality } from 'src/shared/mappings/criticality';

import { useAppCard, getProtectionLevel, isActiveStatus, isNotAddedStatus, getUnclaimedFooterMode } from '../model';

import { CertificateBadges } from './CertificateBadges';
import { ProductRow } from './ProductRow';
import { UnreadTasksBadge } from './UnreadTasksBadge';

const UnclaimedFooter = () => {
  const { data, onFinishSetup } = useAppCard();
  const mode = getUnclaimedFooterMode(data);

  const handleFinishSetupClick = (event: React.MouseEvent) => {
    event.preventDefault();
    // event.stopPropagation();
    onFinishSetup();
  };

  if (mode === 'awaiting_vendor') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground">
        <Clock className="size-3.5" />
        Awaiting {data.unclaimedDetectedProduct[0]?.vendorName ?? 'the vendor'}
      </span>
    );
  }

  if (mode === 'read_only') return null;

  return (
    <Button variant="secondary" size="sm" onClick={handleFinishSetupClick}>
      <Pencil /> Finish app setup
    </Button>
  );
};

const NoProductsFooter = () => {
  const { onAddProtection } = useAppCard();

  const handleAddProtectionClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onAddProtection?.();
  };

  return (
    <Button variant="secondary" size="sm" onClick={handleAddProtectionClick}>
      <PlusIcon /> Add protection
    </Button>
  );
};

const FooterStart = () => {
  const { data } = useAppCard();

  if (!data.claimed) return <UnclaimedFooter />;
  if (getProtectionLevel(data.products) === 'no_products') return <NoProductsFooter />;

  return <ProductRow />;
};

const FooterEnd = () => {
  const { data } = useAppCard();

  if (getWorstCriticality(data.taskCounts)) return <UnreadTasksBadge />;
  if (!data.claimed || getProtectionLevel(data.products) === 'no_products') return null;
  if (data.products.some((product) => isActiveStatus(product.status))) return <CertificateBadges />;

  const inProgressCount = data.products.filter(
    (product) => !isActiveStatus(product.status) && !isNotAddedStatus(product.status),
  ).length;

  return (
    <span className="text-xs text-muted-foreground shrink-0">
      {inProgressCount} product{inProgressCount === 1 ? '' : 's'} in progress
    </span>
  );
};

const Footer = () => {
  return (
    <div className="space-y-4">
      <Separator data-testid="app-card-divider" />
      <div className="flex items-center justify-between gap-2">
        <FooterStart />
        <FooterEnd />
      </div>
    </div>
  );
};

export { Footer };
